import { Link } from 'react-router-dom';

const DeleteAccount = () => {
  return (
    <div className="min-h-screen bg-frost-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-dark-slate">GradLink</Link>
          <nav className="flex gap-6 text-sm text-slate-gray">
            <Link to="/login" className="hover:text-dark-slate">Sign in</Link>
            <Link to="/privacy" className="hover:text-dark-slate">Privacy</Link>
            <Link to="/terms" className="hover:text-dark-slate">Terms</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-4xl font-bold text-dark-slate mb-3">Delete your GradLink account</h1>
        <p className="text-slate-gray leading-relaxed mb-10">
          You can permanently delete your GradLink account and its associated data at any time.
          There is no waiting period. Deletion is immediate and cannot be undone.
        </p>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-dark-slate mb-3">If you can sign in</h2>
          <ol className="list-decimal list-outside pl-6 space-y-2 text-slate-gray leading-relaxed">
            <li>
              Open the GradLink app (mobile or web) and{' '}
              <Link to="/login" className="text-dark-slate underline">sign in</Link>.
            </li>
            <li>Tap or click your profile, then open <strong>Settings</strong>.</li>
            <li>Scroll to the <strong>Danger Zone</strong> at the bottom of the Account tab.</li>
            <li>Tap <strong>Delete Account</strong>, type <span className="font-mono">DELETE</span> to confirm, then tap <strong>Permanently Delete Account</strong>.</li>
          </ol>
          <p className="text-slate-gray mt-4">
            Your account is removed immediately and you will be signed out on every device.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-dark-slate mb-3">If you cannot sign in</h2>
          <p className="text-slate-gray leading-relaxed">
            Email{' '}
            <a href="mailto:hello@gradlink.co?subject=Account%20deletion%20request" className="text-dark-slate underline">
              hello@gradlink.co
            </a>{' '}
            from the email address on your account with the subject line
            "Account deletion request." We will delete your account within 7 business days
            after we verify the request. You may be asked to confirm ownership of the address.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-dark-slate mb-3">What gets deleted</h2>
          <ul className="list-disc list-outside pl-6 space-y-1 text-slate-gray leading-relaxed">
            <li>Your profile (name, institution, bio, work history, photos, all fields you added)</li>
            <li>Posts, comments, likes, and clips you created</li>
            <li>Direct messages you sent or received</li>
            <li>Dating profile, matches, and mentorship data</li>
            <li>Job posts, applications, saved jobs, and referrals</li>
            <li>Housing, marketplace, and service-provider listings</li>
            <li>Event RSVPs and any events you organized</li>
            <li>Uploaded photos, videos, and files</li>
            <li>Push-notification subscriptions</li>
            <li>Your Firebase Auth sign-in credentials</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-dark-slate mb-3">What is retained (and why)</h2>
          <ul className="list-disc list-outside pl-6 space-y-1 text-slate-gray leading-relaxed">
            <li>
              <strong>Payment and transaction records</strong> — kept for accounting and tax
              purposes as required by U.S. law. These records are de-linked from your identity
              during deletion (the user reference is removed).
            </li>
            <li>
              <strong>Audit-log entries</strong> — a record of the deletion itself (email, name,
              timestamp) is kept in our internal audit log for security and compliance.
            </li>
            <li>
              <strong>Content in aggregate analytics</strong> — anonymized, non-identifying
              usage statistics may persist in aggregate reports.
            </li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-dark-slate mb-3">Questions</h2>
          <p className="text-slate-gray leading-relaxed">
            See our{' '}
            <Link to="/privacy" className="text-dark-slate underline">Privacy Policy</Link>{' '}
            for the full picture of how we handle your data, or email{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>{' '}
            with any questions.
          </p>
        </section>

        <footer className="border-t border-slate-200 pt-6 mt-16 text-sm text-slate-gray flex flex-wrap gap-4 justify-between">
          <span>&copy; {new Date().getFullYear()} GradLink</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-dark-slate">Privacy</Link>
            <Link to="/terms" className="hover:text-dark-slate">Terms</Link>
            <Link to="/login" className="hover:text-dark-slate">Sign in</Link>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default DeleteAccount;
